/* aw-zip-gate.js
 * ZIP delivery-eligibility gate: served (free) / nearby (surcharge) / beyond (waitlist).
 * Data lazy-loads on first focus so it never blocks LCP. Zero network on input.
 * Persists { state, zip, fee } to localStorage + cart attributes; the order widget
 * and checkout enforcement read the same values.
 */
/* Stored-verdict stamp (Brian, 2026-07-21). Bump CACHE_VERSION whenever the
 * meaning of a stored field changes. The stamp rides alongside the resolve()
 * result, never replaces it: both arms read state, zip, fee, miles, cadence and
 * note off this object at boot, so its shape must not change. Declared above
 * init() because init() reads the cache while this module is still evaluating. */
const CACHE_VERSION = 1;
const CACHE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

/* Homepage mount (2026-08-04). The gate now also renders on pages WITHOUT the
 * guided-flow wizard (first: the homepage hero). HAS_FLOW gates the behaviors
 * that only make sense when the wizard owns the post-verdict experience
 * (advance + scroll); GATE_LOCATION rides every trackGate() payload so
 * homepage and order-page gate events stay separable in GTM/GA/Meta without
 * URL inspection. Both set in init(); defaults preserve order-page behavior. */
let HAS_FLOW = true;
let GATE_LOCATION = 'order';

// Up here for the same reason: the returning-visitor restore calls loadData()
// synchronously, before this module finishes evaluating.
let DATA = null;

/* Cart-attribute write queue (Brian, 2026-07-21). persist() and
 * clearEligibility() POST the same five keys, so two checks inside one round
 * trip could land out of order: a waitlist ZIP corrected to a served one could
 * finish with localStorage saying served while the cart carried no Delivery
 * attributes at all, which is also how the Oregon pre-order hold flag goes
 * missing. Chaining every write onto the previous one keeps the cart's order
 * the same as the call order. Declared up here because init() can clear an
 * expired verdict while this module is still evaluating. */
let lastWrite = Promise.resolve();

const root = document.getElementById('aw-zip-gate');
if (root) init(root);

function cfg(el) {
  return {
    servedUrl: el.dataset.servedUrl,
    surchargeUrl: el.dataset.surchargeUrl,
    regionsUrl: el.dataset.regionsUrl,
    notesUrl: el.dataset.notesUrl,
    mode: el.dataset.feeMode,
    flatFee: Number(el.dataset.flatFee),
    per5: Number(el.dataset.per5),
    maxMiles: Number(el.dataset.maxMiles),
    feeCap: Number(el.dataset.feeCap) || 0,
    klaviyoKey: el.dataset.klaviyoKey,
    klaviyoList: el.dataset.klaviyoList,
  };
}

/* Brian, 2026-07-21: a 429 burst, a CDN blip or an HTML 404 body used to reject
 * here, which killed the submit handler as an unhandled rejection and left the
 * customer with no error and no state change. Every fetch now fails soft, the
 * caller shows a retryable message, and a failed load is never cached, so the
 * next Check can still succeed. */
async function loadData(c) {
  if (DATA) return DATA;
  const json = (url) => fetch(url)
    .then((r) => {
      if (!r.ok) throw new Error('HTTP ' + r.status);
      return r.json();
    })
    .catch(() => null);
  const [served, surcharge, regions, notes] = await Promise.all([
    json(c.servedUrl),
    json(c.surchargeUrl),
    c.regionsUrl ? json(c.regionsUrl) : Promise.resolve(null),
    c.notesUrl ? json(c.notesUrl) : Promise.resolve(null),
  ]);
  // Ordering is hard-gated on eligibility, so a missing served/surcharge list is
  // a hard stop, not a degraded result. Regions and notes stay optional.
  if (!Array.isArray(served) || !surcharge || typeof surcharge !== 'object') return null;
  DATA = { served: new Set(served.map(String)), surcharge, regions, notes };
  return DATA;
}

/* Route-zone cadence for a ZIP ({ label, weeks }), or null for the default zones. */
function regionFor(zip, data) {
  const r = data.regions;
  const key = r && r.zips && r.zips[zip];
  return (key && r.regions[key]) || null;
}

/* Coming-soon / informational note for a served ZIP ({ note, cta }), or null.
 * Oregon (2026-07-19, Brian): the 16 Portland-area ZIPs are orderable now but
 * delivery starts Q4 2026, so a served result also shows the note. */
function noteFor(zip, data) {
  const groups = data.notes;
  if (!Array.isArray(groups)) return null;
  for (const g of groups) {
    if (g && g.zips && g.zips.indexOf(zip) > -1) return { note: g.note || '', cta: g.cta || '' };
  }
  return null;
}

function feeForMiles(miles, c) {
  if (miles == null || miles > c.maxMiles) return null;
  if (c.mode === 'flat') return c.flatFee;
  let fee = Math.ceil(miles / 5) * c.per5;
  if (c.feeCap) fee = Math.min(fee, c.feeCap);
  return fee;
}

function resolve(zip, data, c) {
  const region = regionFor(zip, data);
  const cadence = region ? { label: region.label, weeks: region.weeks } : null;
  const note = noteFor(zip, data);
  if (data.served.has(zip)) return { state: 'served', zip, fee: 0, miles: 0, cadence, note };
  const miles = data.surcharge[zip];
  const fee = feeForMiles(miles, c);
  // A ZIP that resolves to a $0 fee (shares a centroid with a served ZIP) is served, not "surcharge $0".
  if (fee === 0) return { state: 'served', zip, fee: 0, miles, cadence, note };
  if (fee != null) return { state: 'surcharge', zip, fee, miles, cadence, note };
  return { state: 'waitlist', zip, fee: null, miles: miles == null ? null : miles, cadence: null };
}

function cadenceSentence(cadence) {
  if (!cadence) return '';
  return ' We deliver to ' + cadence.label + ' once every ' + cadence.weeks + ' weeks.';
}

/* Reads the stored verdict, dropping anything stamped for another schema or
 * past its 30-day life. Entries written before the stamp existed carry neither
 * field; those are migrated rather than dropped, since the ZIP is all the
 * re-resolve needs and dropping them would re-ask every returning visitor for a
 * ZIP they already gave us. */
function readCache() {
  let cached = null;
  try { cached = JSON.parse(localStorage.getItem('aw_delivery')); } catch (_) {}
  if (!cached || !cached.zip) return null;
  if (cached.v == null && cached.exp == null) return cached;
  if (cached.v !== CACHE_VERSION || !(Number(cached.exp) > Date.now())) {
    clearEligibility();
    return null;
  }
  return cached;
}

/* The stored ZIP on its own, with none of readCache()'s version or expiry side
 * effects: the load-failure path only needs to know which ZIP the customer is
 * walking away from, and must not clear anything just by asking. */
function storedZip() {
  try {
    const raw = JSON.parse(localStorage.getItem('aw_delivery'));
    return raw && raw.zip ? String(raw.zip) : null;
  } catch (_) { return null; }
}

/* Drops the verdict from both places it lives. A waitlist ZIP has to clear the
 * cart attributes too (Brian, 2026-07-21): persist() was the only writer, so an
 * earlier served ZIP stayed cached and stayed on the cart, and the customer
 * could subscribe to an address we do not deliver to. Names only its own keys.
 * By default it also fires a null 'aw:eligibility' (Brian, 2026-07-21), for the
 * callers that have no verdict left to announce: the change-ZIP button, an
 * expired or re-stamped cache, and the fail-closed submit path. An arm that read
 * the verdict at boot has no other way to learn it is gone, so its CTA stayed
 * live on a verdict that no longer existed.
 * The waitlist callers pass { silent: true }: render() has just dispatched the
 * waitlist verdict, and a null one tick later would wipe the very verdict both
 * arms need to draw their waitlist state. They still get the cache drop and the
 * cart-attribute clear, which is the part they actually need. */
async function clearEligibility(opts) {
  try { localStorage.removeItem('aw_delivery'); } catch (_) {}
  if (!(opts && opts.silent)) document.dispatchEvent(new CustomEvent('aw:eligibility', { detail: null }));
  const write = lastWrite.then(() => fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        'Delivery ZIP': '',
        'Delivery Eligibility': '',
        'Delivery Fee': '',
        'Delivery Cadence': '',
        'Delivery Note': '',
      },
    }),
  })).catch(() => { /* non-blocking, and the queue must survive it */ });
  lastWrite = write;
  await write;
}

async function persist(res) {
  const stored = Object.assign({}, res, { v: CACHE_VERSION, exp: Date.now() + CACHE_MAX_AGE_MS });
  try { localStorage.setItem('aw_delivery', JSON.stringify(stored)); } catch (_) {}
  const write = lastWrite.then(() => fetch('/cart/update.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      attributes: {
        'Delivery ZIP': res.zip,
        'Delivery Eligibility': res.state,
        'Delivery Fee': res.fee == null ? '' : String(res.fee),
        'Delivery Cadence': res.cadence ? 'once every ' + res.cadence.weeks + ' weeks (' + res.cadence.label + ')' : '',
        // Flags Oregon pre-orders so ops can hold them for the Q4 2026 launch.
        'Delivery Note': res.note && res.note.note ? res.note.note : '',
      },
    }),
  })).catch(() => { /* non-blocking, and the queue must survive it */ });
  lastWrite = write;
  await write;
}

/* Same customer-facing money format as aw-money.liquid / usd() in the other
 * aw-* modules (fees are whole dollars, so decimals only ever appear if the
 * fee config changes to a fractional value). */
function money(n) {
  const v = Number(n);
  const digits = v % 1 === 0 ? 0 : 2;
  return '$' + v.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: 2 });
}

/* ---- Analytics + reveal helpers (2026-08-04) -------------------------------
 * Added after E4 moved the ZIP gate above the mobile fold. Two problems that
 * publish created or exposed:
 *
 * 1. NOTHING COUNTED GATE USAGE. There was no event of any kind on submit, so a
 *    rejected visitor left no trace anywhere unless they also typed an email
 *    into the waitlist. The fold fix was justified on heatmap scroll data, and
 *    the follow-up measurement had nothing to read. trackGate() fires exactly
 *    one event per submit, carrying only the verdict.
 *
 *    PRIVACY, deliberate: the ZIP itself is NOT sent. A 5-digit ZIP plus a
 *    timestamp is quasi-identifying, none of these sinks are covered by the
 *    consent work still open with Chris, and the verdict alone answers the
 *    question being asked (how many people use the gate, and what share are
 *    turned away). Do not "improve" this by adding the ZIP.
 *
 * 2. THE ANSWER LANDED BELOW THE FOLD. The gate card floats over the photo on
 *    phones, but the RESULT renders back in normal flow beneath it. Measured on
 *    live E4: on a 375x559 viewport the answer landed at 814px, 256px below the
 *    fold, with no scroll. Tapping Check read as "nothing happened".
 *    revealResult() fixes that, but only when the result is genuinely off
 *    screen AND only for the states where the guided flow does not already own
 *    the scroll.
 */
function trackGate(name, params) {
  const payload = Object.assign({ gate: 'zip', location: GATE_LOCATION }, params || {});
  // Each sink is optional and independently guarded. This is measurement: a
  // missing or blocked tag must never break a customer's ZIP check.
  try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: name }, payload)); } catch (_) {}
  try { if (typeof window.gtag === 'function') window.gtag('event', name, payload); } catch (_) {}
  try { if (typeof window.fbq === 'function') window.fbq('trackCustom', name, payload); } catch (_) {}
  try {
    const sa = window.Shopify && window.Shopify.analytics;
    if (sa && typeof sa.publish === 'function') sa.publish(name, payload);
  } catch (_) {}
}
// 2026-08-04: exported so the guided flow can reuse the same four sinks for
// per-step events (gf_step_view). Payload's own `gate` key overrides the default.
window.awTrackGate = trackGate;

function revealResult(el) {
  if (!el || el.hidden) return;
  // Wait a frame: render() has just un-hidden the result, dispatched
  // aw:eligibility (which can collapse the hero) and may have expanded the
  // waitlist form, so measuring now would measure the pre-layout position.
  requestAnimationFrame(() => {
    const r = el.getBoundingClientRect();
    const vh = window.innerHeight || document.documentElement.clientHeight;
    // Already fully visible: scrolling would yank the page for no reason.
    if (r.top >= 0 && r.bottom <= vh) return;
    let reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (_) {}
    try { el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' }); }
    catch (_) { el.scrollIntoView(); } // older Safari has no options overload
  });
}

function render(res, els) {
  const { result, waitlist, changeBtn, cta } = els;
  waitlist.hidden = true;
  if (cta) cta.hidden = true;
  result.hidden = false;
  changeBtn.hidden = false;
  if (res.state === 'served') {
    result.className = 'aw-gate__result is-yes';
    if (res.note && res.note.note) {
      // Orderable now, but the note sets delivery expectations (Oregon Q4 2026).
      result.textContent = res.note.note + (res.note.cta ? ' ' + res.note.cta : '');
    } else {
      result.textContent = res.cadence
        ? 'Free delivery to ' + res.cadence.label + ', once every ' + res.cadence.weeks + ' weeks.'
        : 'We deliver to you. Delivery is free.';
    }
    // No wizard on this page: a bare verdict is a dead end, so surface the path forward.
    if (cta && !HAS_FLOW) cta.hidden = false;
  } else if (res.state === 'surcharge') {
    result.className = 'aw-gate__result is-fee';
    result.textContent = 'Good news, we deliver to you. You are just outside our core area, so a ' + money(res.fee) + ' delivery fee applies to each delivery.' + cadenceSentence(res.cadence);
    if (cta && !HAS_FLOW) cta.hidden = false;
  } else {
    result.className = 'aw-gate__result is-no';
    result.textContent = 'We are not in ' + res.zip + ' yet. Add your email and we will tell you the moment we are.';
    waitlist.hidden = false;
  }
  document.dispatchEvent(new CustomEvent('aw:eligibility', { detail: res }));
}

/* Lookup data would not load. Same affordance as the 5-digit error above, and
 * Check stays live so the customer can retry. */
function renderLoadError(result) {
  result.hidden = false;
  result.className = 'aw-gate__result is-no';
  result.textContent = 'Something went wrong on our end, please tap Check to try again.';
}

async function joinWaitlist(res, c, emailInput) {
  const email = (emailInput.value || '').trim();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) { emailInput.focus(); return false; }
  if (!c.klaviyoKey || !c.klaviyoList) return false;
  const body = {
    data: {
      type: 'subscription',
      attributes: {
        profile: {
          data: {
            type: 'profile',
            attributes: {
              email,
              properties: { waitlist_zip: res.zip, source: 'zip-gate waitlist' },
            },
          },
        },
      },
      relationships: { list: { data: { type: 'list', id: c.klaviyoList } } },
    },
  };
  try {
    const r = await fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + c.klaviyoKey, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2024-10-15' },
      body: JSON.stringify(body),
    });
    return r.ok || r.status === 202;
  } catch (_) { return false; }
}

function init(el) {
  const c = cfg(el);
  const form = document.getElementById('aw-zip-form');
  const input = document.getElementById('aw-zip-input');
  const result = document.getElementById('aw-zip-result');
  const waitlist = document.getElementById('aw-zip-waitlist');
  const emailInput = document.getElementById('aw-zip-email');
  const joinBtn = document.getElementById('aw-zip-join');
  const changeBtn = document.getElementById('aw-zip-change');
  const cta = document.getElementById('aw-zip-cta');
  const els = { result, waitlist, changeBtn, cta };

  // Wizard detection: the guided flow ships as its own script tag in the
  // order-page section's initial HTML (verified absent on the homepage,
  // 2026-08-04). When absent, this gate owns the post-verdict reveal + CTA.
  HAS_FLOW = !!document.querySelector('script[src*="aw-guided-flow"]');
  GATE_LOCATION = el.dataset.gateLocation || 'order';

  input.addEventListener('focus', () => loadData(c), { once: true });

  // Returning visitor: show their saved state, offer a change link, never re-ask.
  // Brian, 2026-07-21: the saved ZIP is re-resolved against current data instead
  // of replayed, so a ZIP that moved onto a new route (33149 to the 2-week
  // key-biscayne run, 90042 to weekly) self-heals on the next visit. Nothing
  // renders until the verdict is current, so no wrong cadence flashes first.
  // The re-resolved result is then re-asserted onto this visit's cart, which is
  // how the Delivery Note (the Oregon pre-order hold flag) reaches a cart the
  // visitor did not check their ZIP on.
  // If the visitor checks a ZIP or clears theirs while the restore is still in
  // flight, the restore must not land on top of that newer verdict.
  let restoreDropped = false;

  async function restore(cached) {
    const data = await loadData(c);
    if (restoreDropped) return;
    if (!data) {
      // Data unreachable: fall back to the stored verdict rather than blanking a
      // returning visitor. Not persisted, since it could not be re-checked.
      render(cached, els);
      return;
    }
    const res = resolve(cached.zip, data, c);
    render(res, els);
    // Silent: render() just dispatched this waitlist verdict, and the arms have
    // to keep it to draw their waitlist state.
    if (res.state === 'waitlist') clearEligibility({ silent: true });
    else persist(res);
  }

  const cachedRes = readCache();
  if (cachedRes) {
    // Brian, 2026-07-21: restore() is fire-and-forget, so its promise is caught
    // here. A malformed cached entry must not take the rest of init() (the
    // change button and the submit handler) down with it, and must not surface
    // as an unhandled rejection.
    try {
      input.value = cachedRes.zip;
      restore(cachedRes).catch(() => {});
    } catch (_) { /* boot continues without the restore */ }
  }

  changeBtn.addEventListener('click', () => {
    restoreDropped = true;
    input.value = '';
    result.hidden = true;
    waitlist.hidden = true;
    changeBtn.hidden = true;
    if (cta) cta.hidden = true;
    // Brian, 2026-07-21: this used to hand-roll half a clear, dropping the cache
    // but leaving the Delivery* attributes on the cart, so a customer who
    // changed their ZIP still checked out against the old verdict. One
    // implementation now. Not silent: there is a real removal to announce.
    clearEligibility();
    input.focus();
  });

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    restoreDropped = true;
    const zip = (input.value || '').replace(/\D/g, '').slice(0, 5);
    if (zip.length !== 5) {
      result.hidden = false;
      result.className = 'aw-gate__result is-no';
      result.textContent = 'Please double-check your ZIP, it should be 5 digits.';
      trackGate('zip_gate_submit', { result: 'invalid' });
      revealResult(result);
      return;
    }
    const data = await loadData(c);
    if (!data) {
      /* Brian, 2026-07-21: fail closed, but only for a ZIP the customer has
       * actually changed. Typing a different ZIP is them telling us the saved
       * verdict belongs to an address they have left, so it goes and both arms
       * re-block until a real answer lands. A blip while re-checking the SAME
       * ZIP keeps the saved verdict: it is still the right answer for that ZIP,
       * and blocking every session on a CDN hiccup costs more than it protects.
       * The trade-off left standing: a first-ever visitor whose lookup never
       * loads has no verdict to clear, and the arms already block on that. */
      const prev = storedZip();
      if (prev && prev !== zip) clearEligibility();
      renderLoadError(result);
      trackGate('zip_gate_submit', { result: 'error' });
      revealResult(result);
      return;
    }
    const res = resolve(zip, data, c);
    render(res, els);
    trackGate('zip_gate_submit', { result: res.state });
    // A waitlist verdict clears the saved one instead of writing nothing, or the
    // previously checked served ZIP survives into the next visit. Silent, since
    // render() has already dispatched the waitlist verdict the arms render from.
    if (res.state === 'waitlist') {
      // Only the non-orderable verdicts need the reveal. For served/surcharge the
      // guided flow advances to the household step and focuses its heading, which
      // scrolls there itself - calling revealResult too would fight it.
      revealResult(result);
      clearEligibility({ silent: true });
    } else {
      persist(res);
      // No wizard here (homepage): nothing advances or scrolls on its own, so
      // the served/surcharge verdict + CTA get the same reveal the waitlist
      // branch has. On the order page the guided flow owns this scroll.
      // Reveal the CTA when shown, not the result: on short viewports (SE,
      // 559px) the verdict line is visible but the CTA under it is not, and
      // centering the CTA keeps the verdict on screen too (measured 2026-08-04).
      if (!HAS_FLOW) revealResult(cta && !cta.hidden ? cta : result);
    }
  });

  joinBtn.addEventListener('click', async () => {
    const zip = (input.value || '').replace(/\D/g, '').slice(0, 5);
    const data = await loadData(c);
    if (!data) { renderLoadError(result); return; }
    const res = resolve(zip, data, c);
    joinBtn.disabled = true;
    const ok = await joinWaitlist(res, c, emailInput);
    joinBtn.disabled = false;
    result.hidden = false;
    result.className = 'aw-gate__result ' + (ok ? 'is-yes' : 'is-no');
    result.textContent = ok
      ? 'You are on the list. We will email you the moment we deliver to ' + res.zip + '.'
      : 'Something went wrong, please try again.';
    trackGate('zip_gate_waitlist_join', { result: ok ? 'ok' : 'error' });
    // The confirmation renders in #aw-zip-result, which sits ABOVE the email
    // field the customer just used, so after scrolling down to type it can land
    // off screen upward.
    revealResult(result);
  });
}
